import { useEffect, useState } from 'react';
import { Tag, Typography } from 'antd';
import { CreditCardOutlined, BankOutlined, HomeOutlined } from '@ant-design/icons';
import AvaProductUsage from './AvaProductUsage';
import SaveAndBuildCredit from './SaveAndBuildCredit';
import RentReportingComponent from './RentReportingComponent';
import LoaderComponent from '../../Loader';
import usePageState from '../../../helpers/usePageState';
import { convertCentsIntoDollar, formatDate, getLatestLoan, toWriteOff } from '../../../helpers/genericHelper';
import API from '../../../services/API';

const { Text } = Typography;

const statusColor = (status) => {
  switch (status) {
    case 'ACTIVE':
    case 'IN_PROGRESS':
    case 'COMPLETED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'DELINQUENT':
      return 'error';
    case 'CHARGED_OFF':
    case 'WRITE OFF':
    case 'CANCELLED':
    case 'CLOSED':
      return 'error';
    default:
      return 'default';
  }
};

// One card per product, used to switch between them. Shows each product's status at a glance.
const ProductTab = ({ active, icon, title, status, detail, onClick }) => (
  <div className={`summary-product-tab${active ? ' active' : ''}`} onClick={onClick} role="tab" aria-selected={active}>
    <Text strong style={{ fontSize: 15 }}>{icon} {title}</Text>
    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
      {status && <Tag color={statusColor(status)}>{status}</Tag>}
      {/* Amount due / installments paid: large and dark so it reads at a glance */}
      {detail && <Text strong style={{ fontSize: 16, color: '#1f1f1f' }}>{detail}</Text>}
    </div>
  </div>
);

export default function Products({
  loading,
  summaryData,
  creditCards,
  getData,
  userId,
  getAllCreditCardData,
  ccDomains,
  loanDetails,
  paymentAttempts,
  autoPayStatus
}) {
  const [activeProduct, setActiveProduct] = usePageState('products-tab', '1');

  // Tradeline numbers come from the furnishing data. The account summary's copy can be missing or partial,
  // so fetch the full details the same way the Furnishment tab does, and use the summary's copy until then.
  const [fetchedFurnishing, setFetchedFurnishing] = useState(null);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setFetchedFurnishing(null);
    API.getFurnishingDetails({ userId })
      .then((details) => { if (!cancelled) setFetchedFurnishing(details); })
      .catch((error) => console.error('Unable to load furnishing details for tradeline numbers', error));
    return () => { cancelled = true; };
  }, [userId]);
  const furnishing = fetchedFurnishing || summaryData?.furnishingResponse;

  const latestCard = ccDomains?.find((domain) => domain?.account?.accountStatus === 'ACTIVE') ?? ccDomains?.[0];
  const cardAmountDue = latestCard?.domain?.balances?.currentAmountDue;

  // Status from the loan details, the same data the Save & Build page shows; the summary's copy is only a fallback
  const summaryPlans = summaryData?.saveAndBuildCredit || [];
  const latestLoanDetails = getLatestLoan(loanDetails, summaryPlans.map((plan) => plan?.sbLoanEntry?.id));
  const latestSummaryPlan = [...summaryPlans]
    .sort((a, b) => new Date(b.sbLoanEntry?.createTs) - new Date(a.sbLoanEntry?.createTs))[0];
  const latestPlan = latestLoanDetails
    ? summaryPlans.find((plan) => plan?.sbLoanEntry?.id === latestLoanDetails.id)
    : latestSummaryPlan;
  const loanStatus = latestLoanDetails?.loanStatus || latestSummaryPlan?.sbLoanEntry?.loanStatus;
  const loanTerm = latestLoanDetails?.type?.term ?? loanDetails?.[latestSummaryPlan?.sbLoanEntry?.id]?.loanDetails?.type?.term;
  const loanPaid = loanTerm && latestPlan?.remainingMonths != null ? loanTerm - latestPlan.remainingMonths : null;
  // A closed plan shows when it closed (and how many plans there have been) instead of installments paid
  const loanClosedOn = latestLoanDetails?.loanClosureTs || latestPlan?.closureDate;
  const loanDetail = loanClosedOn
    ? <>Closed {formatDate(loanClosedOn)}{summaryPlans.length > 1 && <Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}> · {summaryPlans.length} plans</Text>}</>
    : loanPaid != null ? `${loanPaid} of ${loanTerm} paid` : null;

  return <>
    {
      loading ? <LoaderComponent /> :
        (
         <>
            <div className="summary-product-tabs" role="tablist">
              <ProductTab
                active={activeProduct === '1'}
                onClick={() => setActiveProduct('1')}
                icon={<CreditCardOutlined />}
                title="Ava Credit Card"
                status={latestCard?.account?.accountStatus || 'NONE'}
                detail={cardAmountDue > 0 ? `${convertCentsIntoDollar(cardAmountDue)} due` : null} />
              <ProductTab
                active={activeProduct === '2'}
                onClick={() => setActiveProduct('2')}
                icon={<BankOutlined />}
                title="Save & Build Credit"
                status={toWriteOff(loanStatus) || 'NONE'}
                detail={loanDetail} />
              <ProductTab
                active={activeProduct === '3'}
                onClick={() => setActiveProduct('3')}
                icon={<HomeOutlined />}
                title="Rent Reporting" />
            </div>

            {activeProduct === '1' &&
              <AvaProductUsage
                furnishingAccounts={furnishing?.creditCardFurnishingAccounts}
                creditCards={creditCards}
                ccDomains={ccDomains}
                loading={loading}
                refreshList={getData}
                userSummary={summaryData.userSummary}
                stripeCards={summaryData?.stripeCards}
                getAllCreditCardData={getAllCreditCardData}
                paymentAttempts={paymentAttempts}
                autoPayStatus={autoPayStatus} />
            }
            {activeProduct === '2' &&
              <SaveAndBuildCredit
                furnishingAccounts={furnishing?.savingsBuilderFurnishingAccounts}
                buildCredits={summaryData.saveAndBuildCredit}
                userSummary={summaryData.userSummary}
                loading={loading}
                refreshList={getData}
                loanDetails={loanDetails}
                stripeCards={summaryData?.stripeCards}
                paymentAttempts={paymentAttempts}
                autoPayStatus={autoPayStatus} />
            }
            {activeProduct === '3' &&
              <RentReportingComponent userId={userId} />
            }
         </>
        )
    }
  </>
}
